import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { OtpInput } from "../components/OtpInput";
import { Icon } from "../components/Icon";
import { ApiError, api } from "../lib/api";
import { useRedirectTarget } from "../lib/navigation";
import { useSession } from "../lib/session";
import { copyText, downloadText } from "../lib/clipboard";
import { chunkSecret } from "../lib/format";
import type { EnrolStartResponse } from "../lib/types";

export default function MfaEnrol() {
  const { applyStep, user } = useSession();
  const navigate = useNavigate();
  const from = useRedirectTarget();

  const [enrolment, setEnrolment] = useState<EnrolStartResponse | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const started = useRef(false);

  const start = useCallback(async () => {
    setStartError(null);
    setEnrolment(null);
    try {
      setEnrolment(await api.mfaEnrolStart());
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "already_enrolled") {
        applyStep("password", true);
        navigate("/mfa", { replace: true });
        return;
      }
      setStartError(
        cause instanceof ApiError ? cause.message : "Two-factor setup could not be started.",
      );
    }
  }, [applyStep, navigate]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, [start]);

  const confirm = useCallback(async (value: string) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.mfaEnrolConfirm(value);
      setRecoveryCodes(result.recoveryCodes);
    } catch (cause) {
      const apiError = cause instanceof ApiError ? cause : null;
      setError(apiError?.message ?? "That code could not be confirmed.");
      setCode("");
      if (apiError?.code === "enrolment_expired") void start();
    } finally {
      setBusy(false);
    }
  }, [busy, start]);

  // ---------------------------------------------------------------- recovery codes
  if (recoveryCodes) {
    const asText = [
      "The Terraformed Life — recovery codes",
      user?.email ? `Account: ${user.email}` : "",
      "",
      "Each code works once, in place of a code from your authenticator app.",
      "Keep them somewhere you can reach without your phone.",
      "",
      ...recoveryCodes,
      "",
    ].filter((line) => line !== undefined).join("\n");

    return (
      <AuthLayout
        heading="Save your recovery codes"
        blurb="Two-factor authentication is on. These ten codes are the only way back in if you lose your authenticator app — they are shown once."
      >
        <ol className="recovery-codes">
          {recoveryCodes.map((recovery, index) => (
            <li key={recovery}>
              <b>{index + 1}</b>
              <span>{recovery}</span>
            </li>
          ))}
        </ol>

        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => downloadText("terraformed-life-recovery-codes.txt", asText)}
          >
            <Icon name="download" />
            Download
          </button>
          <button
            type="button"
            className="btn"
            onClick={async () => {
              const ok = await copyText(recoveryCodes.join("\n"));
              setCopied(ok);
            }}
          >
            <Icon name="copy" />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="hint" aria-live="polite">
          {copied ? "All ten codes are on your clipboard." : " "}
        </p>

        <label className="checkline">
          <input
            type="checkbox"
            checked={saved}
            onChange={(event) => setSaved(event.target.checked)}
          />
          <span>I have saved these codes somewhere safe.</span>
        </label>

        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={!saved}
          onClick={() => {
            applyStep("full", true);
            navigate(from ?? "/", { replace: true });
          }}
        >
          Start the course
        </button>
      </AuthLayout>
    );
  }

  // ---------------------------------------------------------------- enrolment
  return (
    <AuthLayout
      heading="Set up two-factor authentication"
      blurb="Scan the square with an authenticator app, then type the six-digit code it shows."
    >
      {startError ? (
        <>
          <p className="form-error" role="alert">
            {startError}
          </p>
          <button type="button" className="btn" onClick={() => void start()}>
            <Icon name="refresh" />
            Try again
          </button>
        </>
      ) : !enrolment ? (
        <p className="row dim" aria-live="polite">
          <span className="spinner" />
          Preparing your setup…
        </p>
      ) : (
        <>
          <div className="mfa-grid">
            <div
              className="qr"
              /* The SVG is generated by our own Worker from the otpauth URL. */
              dangerouslySetInnerHTML={{ __html: enrolment.qrSvg }}
              aria-hidden="true"
            />
            <div className="stack-tight">
              <p className="label">Or type this secret in by hand</p>
              <div className="secret-chunks">
                {chunkSecret(enrolment.secret).map((chunk, index) => (
                  <span key={`${chunk}-${index}`}>{chunk}</span>
                ))}
              </div>
              <div className="row">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={async () => setCopied(await copyText(enrolment.secret))}
                >
                  <Icon name="copy" />
                  {copied ? "Copied" : "Copy secret"}
                </button>
              </div>
              <p className="hint">
                Time-based, six digits, thirty-second period — the default for every authenticator
                app.
              </p>
            </div>
          </div>

          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void confirm(code);
            }}
          >
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="field">
              <span className="label" id="enrol-code-label">
                Code from your app
              </span>
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={(value) => void confirm(value)}
                disabled={busy}
                autoFocus
                invalid={!!error}
                label="Six-digit code from your authenticator app"
                describedBy="enrol-code-label"
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={busy || code.length !== 6}
            >
              {busy ? <span className="spinner" /> : null}
              {busy ? "Checking…" : "Confirm and continue"}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
